import React from 'react';

import d2d from '@assets/img/icons/d2d-white.png';
import { Alert } from '@components/Alert';
import { Button } from '@components/Button';
import { CookiePolicy } from '@components/CookiePolicy';
import { TextInput } from '@components/Forms/TextInput';
import { SpinnerModal } from '@components/SpinnerModal';
import { useJwtLogin } from '@hooks/mutation/useJwtLogin';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import { AuthManager } from '@store/AuthManager';
import { makeMuiStyles, useCommonStyles, useMuiTheme } from '@styles';
import { FormError } from '@utils/errors';
import { formStrings, createValidate, requiredStringField } from '@utils/forms';
import { createStrings } from '@utils/local';
import { PageMeta, url } from '@utils/pages';
import { Form, Formik } from 'formik';
import * as t from 'io-ts';
import { Link, useHistory } from 'react-router-dom';

const strings = createStrings({
  ja: {
    title: '登录',
    forgetPassword: 'パスワードをお忘れの方',
    needCookies: 'クッキーを利用しない場合は、ログインできません。',
    keepLogin: ({ checkboxLable }: any) => (
      <span>
        <a className={checkboxLable}>ログインしたのまま</a>
      </span>
    ),
  },
});

const initialValues: t.TypeOf<typeof schema> = {
  email: '',
  password: '',
};

export const Login: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { mutateAsync, isLoading, isError, error, reset } = useJwtLogin();

  const history = useHistory();
  const theme = useMuiTheme();

  return (
    <div className={classes.container}>
      <SpinnerModal open={isLoading} />

      <div className={classes.middleContainer}>
        <Formik
          validate={createValidate(schema)}
          initialValues={initialValues}
          onSubmit={async (values) => {
            if (AuthManager.shared.cookieAccepted === 'false') {
              // eslint-disable-next-line no-alert
              alert(strings.needCookies);
              AuthManager.shared.setCookieDismissed(false);
              return;
            }

            await mutateAsync(values);
            history.push('/admin');
          }}
        >
          <>
            <Alert open={isError} severity="error" onClose={reset}>
              {error instanceof FormError && error.getDisplayMessage()}
            </Alert>
            <Form className={classes.formContainer}>
              <img src={d2d} className={classes.logo} alt="D2D Logo" />
              <div className={classes.title}>管理システム</div>
              <TextInput
                name="email"
                type="email"
                autoComplete="current-email"
                placeholder={formStrings.email}
                noLabel
                iconBefore={<PersonOutlineIcon className={classes.personIcon} />}
              />
              <TextInput
                name="password"
                type="password"
                autoComplete="current-password"
                placeholder={formStrings.password}
                noLabel
                iconBefore={<LockOutlinedIcon className={classes.personIcon} />}
              />

              <Button
                text={strings.title}
                color="default"
                className={classes.loginButton}
                type="submit"
              />

              <div className={classes.tipsContainer}>
                <a className={classes.forgetPassword}>
                  <FormControlLabel
                    value="end"
                    control={
                      <Checkbox
                        style={{ color: theme.palette.secondary.contrastText }}
                        color="primary"
                      />
                    }
                    label={strings.keepLogin(classes)}
                    labelPlacement="end"
                  />
                </a>
                <a className={classes.forgetPassword}>
                  <Link to={url('/auth/ForgetPassword')}>{strings.forgetPassword}</Link>
                </a>
              </div>
            </Form>
          </>
        </Formik>
      </div>

      <CookiePolicy />
    </div>
  );
};

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexCenter,
    backgroundColor: theme.palette.text.primary,
    flex: 1,
    height: '100vh',
  },
  middleContainer: {
    width: 509,
    height: 600,
    padding: '1rem',
    background: theme.palette.primary.main,
  },
  formContainer: {
    ...common.flexColumn,
    ...common.verticalSpace2,
    padding: '77px 69px',
    marginTop: 30,
  },
  logo: {
    display: 'block',
    width: 182,
    height: 52,
    alignSelf: 'center',
  },
  title: {
    ...common.normalText,
    ...common.flexCenter,
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.palette.secondary.contrastText,
  },
  personIcon: {
    paddingRight: 10,
    width: '1.6rem',
    height: '1.6rem',
    color: theme.palette.grey[500],
  },
  loginButton: {
    borderRadius: 27,
  },
  tipsContainer: {
    ...common.flexCenter,
    ...common.flexColumn,
  },
  forgetPassword: {
    color: theme.palette.secondary.contrastText,
  },
  keepLogin: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.secondary.contrastText,
    ...common.flexInline,
    flex: 1,
    justifyContent: 'space-around',
  },
  middleTextImportant: {
    ...common.normalText,
    fontSize: 18,
    color: theme.palette.secondary.contrastText,
  },
  checkboxLable: {
    fontWeight: 400,
  },
}));

const schema = t.type({
  email: requiredStringField(formStrings.email),
  password: requiredStringField(formStrings.password),
});

export const meta: PageMeta = {};
